import {
    Box,
    Button,
    Divider,
    Modal,
    ModalBody,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    Spinner,
    Stack,
    Text,
    VStack,
} from "@chakra-ui/react";
import { colors } from "../../../../styles";
import { IBlockingProfile, IBlockingProfileList } from "../../../../types";
import { InfiniteData, useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getBlockingProfiles, toggleBlockProfile } from "../../../../api";
import ProfileListItem from "../../../../components/Chat/ProfileListItem";
import ConfirmAlert, { useConfirmAlert } from "../../../../components/Chat/ConfirmAlert";
import { useState } from "react";

interface BlockingModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export default function BlockingModal(props: BlockingModalProps) {

    /* query */

    const queryClient = useQueryClient();
    const { showConfirm, confirmAlertProps } = useConfirmAlert();
    const [unblockingNickname, setUnblockingNickname] = useState<string | null>(null);

    const size = 10;

    const {
        data: blockingProfileListData,
        isLoading: isBlockingProfileListLoading,
        fetchNextPage: fetchNextBlockingProfileList,
        hasNextPage: hasNextBlockingProfileListPage,
        isFetchingNextPage: isFetchingNextBlockingProfileListPage,
    } = useInfiniteQuery<IBlockingProfileList>({
        queryKey: ["getBlockingProfiles"],
        queryFn: ({ pageParam }) => {
            const page = pageParam as number;

            return getBlockingProfiles({
                page,
                size,
            });
        },
        initialPageParam: 1,
        enabled: props.isOpen,
        getNextPageParam: (lastPageData: IBlockingProfileList) => {
            return lastPageData.hasNextPage ? lastPageData.currentPage + 1 : undefined;
        },
    });

    const blockingProfiles: IBlockingProfile[] =
        blockingProfileListData?.pages.flatMap((page) => page.blockingProfiles) ?? [];

    /* toggleBlockProfileMutation */

    const toggleBlockProfileMutation = useMutation({
        mutationFn: toggleBlockProfile,
        onMutate: ({ nickname }) => {
            setUnblockingNickname(nickname);
        },
        onSuccess: (_, { nickname }) => {
            queryClient.setQueryData<InfiniteData<IBlockingProfileList>>(
                ["getBlockingProfiles"],
                (oldData) => {
                    if (!oldData) return oldData;

                    return {
                        ...oldData,
                        pages: oldData.pages.map((page) => ({
                            ...page,
                            blockingProfiles: page.blockingProfiles.filter(
                                (profile) => profile.nickname !== nickname
                            ),
                            totalCount: Math.max(0, page.totalCount - 1),
                        })),
                    };
                }
            );
            queryClient.invalidateQueries({ queryKey: ["getBlockingProfiles"] });
            queryClient.invalidateQueries({ queryKey: ["chats"] });
        },
        onSettled: () => {
            setUnblockingNickname(null);
        },
        onError: (error) => {
            console.error(error);
        },
    });

    const handleUnblock = (nickname: string) => {
        showConfirm({
            message: "차단을 해제하겠습니까?",
            confirmLabel: "해제",
            onConfirm: () => toggleBlockProfileMutation.mutate({ nickname }),
        });
    };

    /* render */

    const renderList = () => {
        if (isBlockingProfileListLoading) {
            return (
                <VStack width="100%" minH="300px" justifyContent="center" alignItems="center" paddingY={10}>
                    <Spinner color={colors.blue} />
                </VStack>
            );
        }

        if (blockingProfiles.length === 0) {
            return (
                <VStack width="100%" minH="300px" justifyContent="center" alignItems="center" paddingY={10}>
                    <Text fontSize={14} fontWeight={400} color={colors.g3}>
                        차단한 사용자가 없습니다.
                    </Text>
                </VStack>
            );
        }

        return (
            <Stack width="100%" minH="300px" spacing={0}>
                {blockingProfiles.map((profile, index) => (
                    <Box key={profile.id}>
                        <ProfileListItem
                            avatar={profile.avatar}
                            nickname={profile.nickname}
                            userType={profile.user.type}
                            onUnblock={() => handleUnblock(profile.nickname)}
                            isUnblocking={
                                unblockingNickname === profile.nickname &&
                                toggleBlockProfileMutation.isPending
                            }
                        />
                        {index < blockingProfiles.length - 1 && (
                            <Divider borderColor={colors.g5} />
                        )}
                    </Box>
                ))}
            </Stack>
        );
    };

    return (
        <>
            <Modal
                motionPreset="slideInBottom"
                size="md"
                isOpen={props.isOpen}
                onClose={props.onClose}
                isCentered
            >
                <ModalOverlay />
                <ModalContent borderRadius={16} overflow="hidden" maxH="70vh">
                    <ModalHeader
                        display="flex"
                        justifyContent="flex-start"
                        alignItems="center"
                        borderBottom="0.2px solid"
                        borderColor={colors.g4}
                        padding={4}
                        fontSize={16}
                        fontWeight={700}
                        color={colors.g1}
                    >
                        차단한 사용자 보기
                    </ModalHeader>

                    <ModalBody padding={4}>
                        <Box
                            minH="300px"
                            maxH="50vh"
                            overflowY="auto"
                            width="100%"
                        >
                            {renderList()}
                        </Box>

                        {hasNextBlockingProfileListPage && (
                            <Button
                                variant="ghost"
                                width="100%"
                                marginTop={2}
                                onClick={() => fetchNextBlockingProfileList()}
                                isLoading={isFetchingNextBlockingProfileListPage}
                            >
                                더보기
                            </Button>
                        )}
                    </ModalBody>
                </ModalContent>
            </Modal>

            <ConfirmAlert
                {...confirmAlertProps}
                isLoading={toggleBlockProfileMutation.isPending}
            />
        </>
    );
}
